'use client';

/**
 * @packageDocumentation
 * @category Component
 */

import 'BomdisoftComponents/dialog/prompt/styles.css';
import { Button } from 'BomdisoftComponents/buttons/index';
import { Dialog, DialogActions, DialogContent, type DialogSize, DialogTitle } from 'BomdisoftComponents/dialog/index';
import { TextField } from 'BomdisoftComponents/inputs/field/text';
import { cancel, type SupportedLanguage } from '@bomdisoft/translations';
import { type ChangeEvent, type ReactNode, useState } from 'react';

export type PromptDialogProps = {
  cancelLabel?: ReactNode | false;
  confirmLabel: ReactNode;
  description?: ReactNode;
  dismissible?: boolean;
  initialValue?: string;
  label: string;
  language: SupportedLanguage;
  onClose: () => void;
  onConfirm: (value: string) => void;
  open: boolean;
  pending?: boolean;
  placeholder?: string;
  size?: DialogSize;
  testId?: string;
  title: ReactNode;
  validate?: (value: string) => string | null;
};

export const PromptDialog = ({ cancelLabel, confirmLabel, description, dismissible, initialValue, label, language, onClose, onConfirm, open, pending, placeholder, size = 'compact', testId, title, validate }: PromptDialogProps) => {
  const [value, setValue] = useState(initialValue ?? '');
  const message = validate === undefined ? null : validate(value);
  const blocked = value.trim() === '' || message !== null || pending === true;

  const handleClose = () => {
    setValue(initialValue ?? '');
    onClose();
  };

  return (
    <Dialog dismissible={dismissible} form onClose={handleClose} open={open} size={size} testId={testId}>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        {description === undefined ? null : <p className='bomdisoft-prompt-dialog-description'>{description}</p>}
        <TextField autoFocus error={message !== null} helperText={message ?? undefined} label={label} onChange={(event: ChangeEvent<HTMLInputElement>) => setValue(event.target.value)} placeholder={placeholder} type='text' value={value} />
      </DialogContent>
      <DialogActions>
        {cancelLabel === false ? null : (
          <Button color='neutral' data-testid={testId ? `${testId}-cancel` : undefined} onClick={handleClose} variant='text'>
            {cancelLabel ?? cancel[language]}
          </Button>
        )}
        <Button data-testid={testId ? `${testId}-confirm` : undefined} disabled={blocked} onClick={() => onConfirm(value)} variant='contained'>
          {confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
